import { Grid, Typography } from "@mui/material";
import AddButton from "atoms/AddButton";
import CurrencyInput from "atoms/CurrencyInput";
import DeleteRelationButton from "atoms/DeleteRelationButton";
import SimpleMenu from "atoms/SimpleMenu";
import { TaxRateRecordType } from "pages/SharedRecords.types";
import { Fragment } from "react";
import {
  ArrayInput,
  ArrayInputProps,
  NumberInput,
  ReferenceInput,
  required,
  SelectInput,
  SelectInputProps,
  SimpleFormIterator,
  TextInput,
  useChoicesContext,
  useSimpleFormIterator,
  useSimpleFormIteratorItem,
  useTranslate,
} from "react-admin";

function PricePositionMenu(props: { disabled: boolean }) {
  const { disabled } = props;
  const { remove } = useSimpleFormIteratorItem();

  return (
    <SimpleMenu>
      {(closeMenu) => (
        <DeleteRelationButton
          closeMenu={closeMenu}
          disabled={disabled}
          sx={{ marginLeft: 2 }}
          onClick={() => remove()}
          iconButton={false}
        />
      )}
    </SimpleMenu>
  );
}

function PricePositionAddButton() {
  const { add } = useSimpleFormIterator();
  return <AddButton isInPricePositionsInputs onClick={() => add()} />;
}

function TaxRateInputs(props: SelectInputProps) {
  const { allChoices, isLoading } = useChoicesContext<TaxRateRecordType>();

  if (isLoading || !allChoices) return null;
  const defaultTaxRate = allChoices.find((ch) => ch.is_default);

  const renderOptionText = (choice: TaxRateRecordType) => {
    const renderDescription = !!allChoices.find((other) => choice.id !== other.id && choice.amount == other.amount);
    let result = `${choice.amount}%`;
    if (renderDescription) {
      result += ` ${choice.untdid_category_display}`;
    }
    return result;
  };

  return (
    <Fragment>
      <SelectInput
        sx={{ minWidth: 80, maxWidth: 95 }}
        defaultValue={defaultTaxRate?.id}
        optionText={renderOptionText}
        {...props}
      />
    </Fragment>
  );
}

type PricePositionsInputsPropsType = Omit<ArrayInputProps, "children" | "source"> & {
  readOnly?: boolean;
  sourcePrefix?: string;
};

export default function PricePositionsInputs(props: PricePositionsInputsPropsType) {
  const { readOnly = false, sourcePrefix, ...rest } = props;
  const translate = useTranslate();
  const labelPrefix = "resources.price_positions.fields";
  const finalSourcePrefix = sourcePrefix ? `${sourcePrefix}.` : "";

  return (
    <Fragment>
      <Typography variant="body1" fontWeight="500" fontSize="1.1rem" sx={{ marginRight: 2, marginBottom: 3 }}>
        {translate("resources.prices.fields.positions")}
      </Typography>
      <ArrayInput {...rest} source={`${finalSourcePrefix}positions`} validate={required()} label={false}>
        <SimpleFormIterator
          disableAdd={readOnly}
          disableRemove
          disableClear
          disableReordering
          addButton={<PricePositionAddButton />}
          sx={{
            "& .RaSimpleFormIterator-form.RaSimpleFormIterator-inline": {
              flexWrap: "nowrap",
            },
            "& .RaSimpleFormIterator-line": {
              marginBottom: 1.5,
              paddingBottom: 1.5,
              "&:last-child": {
                marginBottom: 0,
              },
            },
            "& .RaSimpleFormIterator-action": { ml: 1 },
          }}
          fullWidth
          inline
          readOnly={readOnly}
        >
          <Grid container columnSpacing={1} rowGap={2}>
            <Grid item xs={12} sm={2}>
              <NumberInput
                label={`${labelPrefix}.quantity`}
                defaultValue={1}
                source="quantity"
                validate={required()}
                helperText={false}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextInput label={`${labelPrefix}.name`} source="name" validate={required()} helperText={false} readOnly={readOnly} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <CurrencyInput
                label={`${labelPrefix}.amount`}
                source="amount"
                validate={[required()]}
                helperText={false}
                readOnly={readOnly}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <ReferenceInput source="tax_rate" reference="tax_rates" sort={{ order: "ASC", field: "amount" }}>
                <TaxRateInputs label={`${labelPrefix}.tax_rate`} validate={required()} helperText={false} readOnly={readOnly} />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} sm={1}>
              <PricePositionMenu disabled={readOnly} />
            </Grid>
          </Grid>
        </SimpleFormIterator>
      </ArrayInput>
    </Fragment>
  );
}
